import { useTranslation } from 'react-i18next';

import { coverageDatesText } from '@/features/coverage/components/coverage-format';
import { useMyApplications, useMyRequests } from '@/features/coverage/hooks/use-coverage';
import { isProvider, useMyProfile } from '@/features/profile';
import { buildIcs, downloadIcs, type IcsEvent } from '@/shared/lib/ics';
import { prayerLabel } from '@/shared/lib/labels';
import { type PrayerSlot } from '@/shared/model/enums';
import { Async } from '@/shared/ui/Async';
import { Button } from '@/shared/ui/Button';
import { useUiStore } from '@/stores/ui.store';

interface Booking {
  id: string;
  title: string;
  city: string | null;
  startDate: string;
  endDate: string;
  prayerSlots: readonly PrayerSlot[];
}

/** Confirmed coverage bookings for both personas, with .ics export (D-011 phase). */
export function CalendarRoute(): JSX.Element {
  const profile = useMyProfile();
  const provider = profile.data ? isProvider(profile.data) : true;
  return provider ? <ProviderCalendar /> : <RequesterCalendar />;
}

function ProviderCalendar(): JSX.Element {
  const query = useMyApplications();
  return (
    <Async query={query}>
      {(apps) => (
        <BookingsView
          bookings={apps
            .filter((a) => (a.status === 'accepted' || a.status === 'completed') && a.request)
            .map((a) => ({
              id: a.id,
              title: a.request?.masjidName ?? '',
              city: a.request?.masjidCity ?? null,
              startDate: a.request?.startDate ?? '',
              endDate: a.request?.endDate ?? '',
              prayerSlots: a.request?.prayerSlots ?? [],
            }))}
        />
      )}
    </Async>
  );
}

function RequesterCalendar(): JSX.Element {
  const query = useMyRequests();
  return (
    <Async query={query}>
      {(requests) => (
        <BookingsView
          bookings={requests
            .filter((r) => r.status === 'filled')
            .map((r) => ({
              id: r.id,
              title: r.masjidName ?? '',
              city: r.masjidCity,
              startDate: r.startDate,
              endDate: r.endDate,
              prayerSlots: r.prayerSlots,
            }))}
        />
      )}
    </Async>
  );
}

function BookingsView({ bookings }: { bookings: readonly Booking[] }): JSX.Element {
  const { t } = useTranslation();
  const locale = useUiStore((s) => s.locale);

  const eventsOf = (list: readonly Booking[]): IcsEvent[] =>
    list.map((b) => ({
      uid: b.id,
      start: b.startDate,
      end: b.endDate,
      summary: t('calendar.eventTitle', { masjid: b.title }),
      location: b.city ?? undefined,
      description: b.prayerSlots.map((p) => prayerLabel(t, p)).join(', '),
    }));

  return (
    <main className="page stack">
      <h1>{t('calendar.title')}</h1>
      {bookings.length === 0 ? (
        <p className="muted">{t('calendar.empty')}</p>
      ) : (
        <>
          <Button
            variant="outline"
            onClick={() => downloadIcs('badeel-coverage', buildIcs(eventsOf(bookings)))}
          >
            {t('calendar.downloadAll')}
          </Button>
          <div className="stack">
            {bookings.map((b) => (
              <div key={b.id} className="card stack">
                <strong>{b.title || t('masjid.fallbackName')}</strong>
                <span>{coverageDatesText(locale, b)}</span>
                <div className="wrap">
                  {b.prayerSlots.map((p) => (
                    <span
                      key={p}
                      className="badge"
                      style={{ background: 'var(--color-surface-2)' }}
                    >
                      {prayerLabel(t, p)}
                    </span>
                  ))}
                </div>
                <Button
                  variant="text"
                  onClick={() => downloadIcs(`badeel-${b.id}`, buildIcs(eventsOf([b])))}
                >
                  {t('calendar.download')}
                </Button>
              </div>
            ))}
          </div>
        </>
      )}
    </main>
  );
}
